<script setup>
import { computed, useTemplateRef } from 'vue';
import { useWindowSize, useElementBounding } from '@vueuse/core';

defineProps({
  option: {
    type: Object,
    default: () => {},
  },
  subMenuAvailable: {
    type: Boolean,
    default: true,
  },
});

const menuRef = useTemplateRef('menuRef');
const { width: windowWidth, height: windowHeight } = useWindowSize();
const { bottom, right } = useElementBounding(menuRef);

// Vertical position
const verticalPosition = computed(() => {
  const SUBMENU_HEIGHT = 240; // 15rem in pixels
  const spaceBelow = windowHeight.value - bottom.value;
  return spaceBelow < SUBMENU_HEIGHT ? 'bottom-0' : 'top-0';
});

// Horizontal position
const horizontalPosition = computed(() => {
  const SUBMENU_WIDTH = 240;
  const spaceRight = windowWidth.value - right.value;
  return spaceRight < SUBMENU_WIDTH
    ? ['right-full', '-translate-x-2']
    : ['left-full', 'translate-x-2'];
});

const submenuPosition = computed(() => [
  verticalPosition.value,
  horizontalPosition.value,
]);
</script>

<template>
  <div
    ref="menuRef"
    class="menu-with-submenu"
    :class="{ 'menu-with-submenu--disabled': !subMenuAvailable }"
  >
    <div class="menu-with-submenu__content">
      <span class="menu-with-submenu__leading">
        <fluent-icon :icon="option.icon" size="14" class="menu-icon" />
      </span>
      <p class="menu-with-submenu__label">{{ option.label }}</p>
    </div>
    <fluent-icon
      icon="chevron-right"
      size="12"
      class="menu-with-submenu__chevron"
    />
    <div
      v-if="subMenuAvailable"
      class="submenu"
      :class="submenuPosition"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
.menu-with-submenu {
  position: relative;
  width: 100%;
  min-width: 14rem;
  gap: 0.65rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.875rem;
  color: var(--context-menu-text-primary) !important;
  transition:
    background-color 0.18s ease,
    color 0.18s ease,
    transform 0.18s ease;
  @apply flex items-center justify-between cursor-pointer;

  &:hover {
    background: var(--context-menu-hover);
    color: var(--context-menu-hover-strong) !important;
    transform: translateX(2px);
  }

  &:hover .menu-with-submenu__leading {
    border-color: rgba(249, 115, 22, 0.2);
    background: rgba(249, 115, 22, 0.1);
    color: var(--context-menu-hover-strong) !important;
  }

  &:hover .menu-with-submenu__label,
  &:hover .menu-with-submenu__chevron {
    color: inherit !important;
  }

  &:hover {
    .submenu {
      @apply block;
    }
  }
}

.menu-with-submenu--disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.menu-with-submenu--disabled:hover {
  transform: none;
  background: transparent;
  color: var(--context-menu-text-primary) !important;
}

.menu-with-submenu--disabled:hover .menu-with-submenu__leading {
  border-color: var(--context-menu-border-soft);
  background: var(--context-menu-surface-secondary);
  color: var(--context-menu-text-secondary) !important;
}

.menu-with-submenu__content {
  min-width: 0;
  @apply flex items-center flex-1;
}

.menu-with-submenu__leading {
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  border: 1px solid var(--context-menu-border-soft);
  border-radius: 0.75rem;
  background: var(--context-menu-surface-secondary);
  color: var(--context-menu-text-secondary) !important;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease;
  @apply inline-flex items-center justify-center;
}

.menu-with-submenu__label {
  margin: 0 0 0 0.65rem;
  min-width: 0;
  color: var(--context-menu-text-primary) !important;
  font-size: var(--wlab-font-md);
  font-weight: 600;
  line-height: 1.2;
  transition: color 0.18s ease;
  @apply truncate;
}

.menu-with-submenu__chevron {
  color: var(--context-menu-text-muted) !important;
  transition: color 0.18s ease;
}

.submenu {
  z-index: 20;
  min-width: 14rem;
  padding: 0.4rem;
  border: 1px solid var(--context-menu-border);
  border-radius: 1rem;
  background: var(--context-menu-surface);
  box-shadow: var(--wlab-shadow-xl);
  backdrop-filter: saturate(180%) blur(18px);
  @apply absolute hidden max-h-[15rem] overflow-y-auto overflow-x-hidden;
}
</style>
